import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../app/store';
import { setCustomerInfo, setQuantity } from './checkoutSlice';
import './Checkout.css';

const CheckoutForm: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const checkoutState = useSelector((state: RootState) => state.checkout);
    const napkinState = useSelector((state: RootState) => state.napkin);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'US',
        },
    });

    const [quantity, setLocalQuantity] = useState(checkoutState.quantity);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [addressField]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newQuantity = parseInt(e.target.value, 10);
        setLocalQuantity(newQuantity);
        dispatch(setQuantity(newQuantity));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(setCustomerInfo(formData));
        // Navigate to payment
        navigate('/payment');
    };

    const calculateTotal = () => {
        return (checkoutState.basePrice * quantity).toFixed(2);
    };

    return (
        <div className="checkout-container">
            <h2>Checkout</h2>
            <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="order-details">
                    <p>Design: {napkinState.image}</p>
                    <p>Text: {napkinState.textLineOne} {napkinState.textLineTwo}</p>
                    <p>Font: {napkinState.font}</p>
                    <p>Color: {napkinState.foilColor}</p>
                </div>
            </div>
            <form onSubmit={handleSubmit} className="checkout-form">
                <div className="form-section">
                    <h3>Customer Information</h3>
                    <div className="form-group">
                        <label htmlFor="name">Full Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                </div>
                <div className="form-section">
                    <h3>Shipping Address</h3>
                    <div className="form-group">
                        <label htmlFor="address.line1">Address Line 1</label>
                        <input
                            type="text"
                            id="address.line1"
                            name="address.line1"
                            value={formData.address.line1}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="address.line2">Address Line 2 (Optional)</label>
                        <input
                            type="text"
                            id="address.line2"
                            name="address.line2"
                            value={formData.address.line2}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="address.city">City</label>
                            <input
                                type="text"
                                id="address.city"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="address.state">State</label>
                            <input
                                type="text"
                                id="address.state"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="address.postal_code">ZIP Code</label>
                            <input
                                type="text"
                                id="address.postal_code"
                                name="address.postal_code"
                                value={formData.address.postal_code}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="form-section">
                    <h3>Order Details</h3>
                    <div className="form-group">
                        <label htmlFor="quantity">Quantity</label>
                        <input
                            type="number"
                            id="quantity"
                            name="quantity"
                            min="50"
                            step="50"
                            value={quantity}
                            onChange={handleQuantityChange}
                            required
                        />
                    </div>
                    <div className="order-total">
                        <span>Total:</span>
                        <span>${calculateTotal()}</span>
                    </div>
                </div>
                <button type="submit" className="checkout-button">
                    Proceed to Payment
                </button>
            </form>
        </div>
    );
};

export default CheckoutForm;
